import React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import { Batch } from '@/types';
import { useI18n } from '@/i18n/I18nContext';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

type BatchCardProps = {
	batch: Batch;
	onClick?: () => void;
};

const statusColors: Record<Batch['status'], 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error'> = {
	planned: 'default',
	brewing: 'primary',
	fermenting: 'warning',
	conditioning: 'secondary',
	completed: 'success',
	archived: 'default'
};

const BatchCard: React.FC<BatchCardProps> = ({ batch, onClick }) => {
	const { t } = useI18n();
	const statusLabel = t.batches.status[batch.status] || batch.status;

	return (
		<Card>
			<CardActionArea onClick={onClick}>
				<CardContent>
					<Stack spacing={2}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="h6" fontWeight={700}>
								{batch.name}
							</Typography>
							<Chip
								label={statusLabel}
								color={statusColors[batch.status]}
								size="small"
							/>
						</Box>
						{batch.recipeName && (
							<Typography variant="body2" color="text.secondary">
								{t.batches.recipe}: {batch.recipeName}
							</Typography>
						)}
						<Stack direction="row" spacing={2} flexWrap="wrap">
							{batch.brewDate && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
									<CalendarTodayIcon fontSize="small" color="action" />
									<Typography variant="caption" color="text.secondary">
										{batch.brewDate}
									</Typography>
								</Box>
							)}
							{batch.abv !== undefined && (
								<Typography variant="caption" color="text.secondary">
									ABV: {batch.abv.toFixed(1)}%
								</Typography>
							)}
							{batch.ibu !== undefined && (
								<Typography variant="caption" color="text.secondary">
									IBU: {batch.ibu}
								</Typography>
							)}
						</Stack>
					</Stack>
				</CardContent>
			</CardActionArea>
		</Card>
	);
};

export default BatchCard;
